import React, { useState, useRef, useEffect } from 'react';
import { useScheme } from '@/context/SchemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import { cn } from '@/components/ui/utils';

const { FiLayers, FiChevronDown, FiCheck, FiSearch } = FiIcons;

const SchemeSelector = () => {
  const { schemes, schemesLoading, selectedSchemeId, selectScheme, selectedScheme } = useScheme();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (schemesLoading) {
    return <div className="h-10 w-48 bg-surface-100 animate-pulse rounded-xl" />;
  }

  if (!schemes.length) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
        <SafeIcon icon={FiLayers} className="w-3.5 h-3.5 text-amber-500" />
        <span className="text-[10px] font-black text-amber-600 uppercase tracking-wider">No Schemes</span>
      </div>
    );
  }

  // Added safety check (s.name || '') to prevent toLowerCase on undefined
  const filteredSchemes = schemes.filter(s => 
    (s.name || '').toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center gap-3 px-4 h-10 rounded-xl border-2 transition-all duration-300 bg-white group",
          isOpen ? "border-primary-500 shadow-glow-sm ring-4 ring-primary-50" : "border-surface-200 hover:border-primary-300 hover:bg-surface-50"
        )}
      >
        <div className={cn(
          "w-6 h-6 rounded-lg flex items-center justify-center transition-colors",
          isOpen ? "bg-primary-600 text-white" : "bg-primary-50 text-primary-600"
        )}>
          <SafeIcon icon={FiLayers} className="w-3.5 h-3.5" />
        </div>
        <div className="flex flex-col items-start min-w-[120px] max-w-[180px]">
          <span className="text-[9px] font-black text-primary-500 uppercase tracking-widest leading-none mb-0.5">
            Active Campaign
          </span>
          <span className="text-xs font-bold text-surface-900 truncate w-full text-left">
            {selectedScheme?.name || 'Select Scheme'}
          </span>
        </div>
        <SafeIcon 
          icon={FiChevronDown} 
          className={cn(
            "w-4 h-4 text-surface-400 transition-transform duration-300",
            isOpen && "rotate-180 text-primary-500"
          )} 
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="absolute right-0 mt-2 w-72 bg-white rounded-2xl shadow-2xl border border-surface-200 z-[100] overflow-hidden"
          >
            {/* Search Header */}
            <div className="p-3 border-b border-surface-100 bg-surface-50/50">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-surface-400" />
                <input 
                  autoFocus
                  type="text" 
                  placeholder="Search schemes..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full h-9 pl-9 pr-4 bg-white border border-surface-200 rounded-lg text-xs font-bold focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-50 transition-all"
                />
              </div>
            </div>

            {/* List */}
            <div className="max-h-[300px] overflow-y-auto p-2 custom-scrollbar">
              {filteredSchemes.length > 0 ? (
                filteredSchemes.map((scheme) => {
                  const isActive = scheme.id === selectedSchemeId;
                  return (
                    <button
                      key={scheme.id}
                      onClick={() => {
                        selectScheme(scheme.id);
                        setIsOpen(false);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 text-left group",
                        isActive ? "bg-primary-50 text-primary-700" : "hover:bg-surface-50 text-surface-600 hover:text-surface-900"
                      )}
                    >
                      <div className="flex flex-col min-w-0">
                        <span className="text-xs font-bold truncate">
                          {scheme.name}
                        </span>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className={cn(
                            "text-[8px] font-black uppercase tracking-tighter px-1.5 py-0.5 rounded",
                            isActive ? "bg-primary-200" : "bg-surface-200"
                          )}>
                            ID: {scheme.id}
                          </span>
                          <span className="text-[9px] font-medium opacity-60">
                            {scheme.status || 'Active'}
                          </span>
                        </div>
                      </div>
                      {isActive && (
                        <motion.div 
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center text-white shrink-0"
                        >
                          <SafeIcon icon={FiCheck} className="w-3 h-3" />
                        </motion.div>
                      )}
                    </button>
                  );
                })
              ) : (
                <div className="py-8 text-center">
                  <p className="text-xs font-bold text-surface-400 italic">No matches found</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-3 bg-surface-50 border-t border-surface-100 flex items-center justify-between">
              <span className="text-[9px] font-black text-surface-400 uppercase tracking-widest">
                {schemes.length} Total Schemes
              </span>
              <div className="flex gap-1">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[9px] font-bold text-emerald-600 uppercase">Live Sync</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SchemeSelector;